import { createApp } from 'vue'
import { createHead } from '@vueuse/head'; // 导入 createHead
import ElementPlus from 'element-plus'
import 'element-plus/es/components/message/style/css'
import 'element-plus/es/components/message-box/style/css'

import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons'; // 引入所有 solid 图标
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import App from './App.vue'
import router from './router'
import store from './store'
import i18n from './i18n'
import { getSeoConfig } from './utils/seoConfig'
import { generateSchemaOrg } from './utils/schemaOrgGenerator'
import { seoMetrics } from './utils/seoMetrics'

import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import 'element-plus/theme-chalk/dark/css-vars.css'
import './styles/global.css'

// OverlayScrollbars 悬浮滚动条
import 'overlayscrollbars/overlayscrollbars.css'


library.add(fas);

const app = createApp(App);
const head = createHead(); // 创建 head 对象

app.component('font-awesome-icon', FontAwesomeIcon);
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
    app.component(key, component)
}

// 根据 useDarkMode 的值添加或移除 dark/light 类
const initDarkModeClass = () => {
    const htmlElement = document.documentElement;
    let isDarkMode;

    // 判断用户是否是自定义模式
    if (store.state.cusDarkMode && store.state.useDarkMode !== null) {
        // 用户手动设置了暗色模式
        isDarkMode = store.state.useDarkMode;
    } else {
        // 跟随系统模式或时间
        isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (!isDarkMode) {
            const now = new Date();
            const hour = now.getHours();
            isDarkMode = hour >= 22 || hour < 6;
        }
        // 更新 useDarkMode 的值
        store.commit('setUseDarkMode', isDarkMode);
    }

    if (isDarkMode) {
        htmlElement.classList.add('dark');
        htmlElement.classList.remove('light');
    } else {
        htmlElement.classList.add('light');
        htmlElement.classList.remove('dark');
    }
};

const applyDarkModeClass = (isDarkMode) => {
    const htmlElement = document.documentElement;
    if (isDarkMode) {
        htmlElement.classList.add('dark');
        htmlElement.classList.remove('light');
    } else {
        htmlElement.classList.add('light');
        htmlElement.classList.remove('dark');
    }
};

// 预设网站标题的函数
const presetSiteTitle = (userConfig, currentLanguage) => {
    const seoConfig = getSeoConfig(currentLanguage || 'zh-CN');
    document.title = userConfig?.siteTitle || seoConfig.title;
};

// 预设网站meta标签的函数
const presetSiteMeta = (userConfig, currentLanguage) => {
    const seoConfig = getSeoConfig(currentLanguage || 'zh-CN');
    const schemaOrg = generateSchemaOrg(currentLanguage || 'zh-CN');
    
    const updateMeta = (name, content) => {
        let meta = document.querySelector(`meta[name="${name}"]`);
        if (!meta) {
            meta = document.createElement('meta');
            meta.name = name;
            document.head.appendChild(meta);
        }
        meta.content = content;
    };

    const updateProperty = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`);
        if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute('property', property);
            document.head.appendChild(meta);
        }
        meta.content = content;
    };

    const updateLink = (rel, href, hreflang = null) => {
        let link = document.querySelector(`link[rel="${rel}"]${hreflang ? `[hreflang="${hreflang}"]` : ''}`);
        if (!link) {
            link = document.createElement('link');
            link.rel = rel;
            if (hreflang) link.hreflang = hreflang;
            document.head.appendChild(link);
        }
        link.href = href;
    };

    const updateScript = (type, content) => {
        let script = document.querySelector(`script[type="${type}"]`);
        if (!script) {
            script = document.createElement('script');
            script.type = type;
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(content);
    };

    // 更新基础meta标签
    updateMeta('description', userConfig?.siteDescription || seoConfig.description);
    updateMeta('keywords', userConfig?.siteKeywords || seoConfig.keywords.join(', '));
    
    // 更新Open Graph标签
    updateProperty('og:title', userConfig?.siteTitle || seoConfig.ogTitle);
    updateProperty('og:description', userConfig?.siteDescription || seoConfig.ogDescription);
    updateProperty('og:locale', seoConfig.locale);
    
    // 更新Twitter Card标签
    updateProperty('twitter:title', userConfig?.siteTitle || seoConfig.ogTitle);
    updateProperty('twitter:description', userConfig?.siteDescription || seoConfig.twitterDescription);
    
    // 更新hreflang标签
    const supportedLanguages = ['zh-CN', 'en-US', 'ja-JP', 'ko-KR', 'fr-FR', 'de-DE', 'es-ES'];
    const baseUrl = 'https://file.heliar.top/';
    
    supportedLanguages.forEach(lang => {
        updateLink('alternate', baseUrl + '?lang=' + lang, lang);
    });
    
    // 更新canonical链接
    updateLink('canonical', baseUrl + '?lang=' + (currentLanguage || 'zh-CN'));
    
    // 更新Schema.org标记
    updateScript('application/ld+json', schemaOrg.webSite);
    updateScript('application/ld+json', schemaOrg.softwareApplication);
};

// 预设网站图标的函数
const presetSiteIcon = (isDarkMode, userConfig) => {
    // 同时更改 icon apple-touch-icon 和 mask-icon
    const existingIcons = document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"], link[rel="mask-icon"]');
    existingIcons.forEach(icon => icon.remove());

    const iconLink = document.createElement('link');
    const appleIconLink = document.createElement('link');
    const maskIconLink = document.createElement('link');
    iconLink.rel = 'icon';
    appleIconLink.rel = 'apple-touch-icon';
    maskIconLink.rel = 'mask-icon';

    if (isDarkMode) {
        iconLink.href = userConfig?.siteIcon || '/logo-dark.png';
        appleIconLink.href = userConfig?.siteIcon || '/logo-dark.png';
        maskIconLink.href = userConfig?.siteIcon || '/logo-dark.png';
    } else {
        iconLink.href = userConfig?.siteIcon || '/logo.png';
        appleIconLink.href = userConfig?.siteIcon || '/logo.png';
        maskIconLink.href = userConfig?.siteIcon || '/logo.png';
    }

    document.head.appendChild(iconLink);
    document.head.appendChild(appleIconLink);
    document.head.appendChild(maskIconLink);
};

store.dispatch('fetchUserConfig').then(() => {
    // 初始化时应用 dark 模式
    initDarkModeClass();

    // 获取当前语言
    const currentLanguage = i18n.global.locale.value || localStorage.getItem('language') || 'zh-CN';

    // 预设网站标题、meta标签和图标
    presetSiteTitle(store.getters.userConfig, currentLanguage);
    presetSiteMeta(store.getters.userConfig, currentLanguage);
    presetSiteIcon(store.state.useDarkMode, store.getters.userConfig);

    // 监听 useDarkMode 和 cusDarkMode 的变化
    store.subscribe((mutation, state) => {
        if (mutation.type === 'setUseDarkMode' && store.state.cusDarkMode) {
            applyDarkModeClass(state.useDarkMode);
            // 同时更新网站图标
            presetSiteIcon(state.useDarkMode, store.getters.userConfig);
        }

        // 监听 cusDarkMode 变化，当设置为 false 时重新初始化
        if (mutation.type === 'setCusDarkMode' && !mutation.payload) {
            // 切换到跟随系统模式，重新初始化
            initDarkModeClass();
            // 同时更新网站图标
            presetSiteIcon(store.state.useDarkMode, store.getters.userConfig);
        }

        // 监听语言切换
        if (mutation.type === 'setLanguage') {
            const newLanguage = mutation.payload || i18n.global.locale.value;
            const oldLanguage = i18n.global.locale.value;
            presetSiteTitle(store.getters.userConfig, newLanguage);
            presetSiteMeta(store.getters.userConfig, newLanguage);
            seoMetrics.trackLanguageSwitch(oldLanguage, newLanguage);
        }
    });

    app.use(store).use(i18n).use(router).use(ElementPlus).use(head).mount('#app');
}).catch(error => {
    console.error('Failed to load user configuration:', error);
    app.use(store).use(i18n).use(router).use(ElementPlus).use(head).mount('#app');
})
